import { useEffect, useState, useContext } from 'react';
import { CLUSTER } from '../../constants';
import ABeagleContext from '../../contexts/abeagle';
import { buildRecsysQuery } from '../../utils/buildRecsysQuery';
import DestinationContext from '../../contexts/destination';
import { stripTags } from '../../utils/sanitize';

const ShoppingPackage = (() => {
  const VARIANT_TO_RECSYS_ENDPOINT = {
    control: 'shopping-package-internal',
    time_spent: 'shopping-package-internal-time-spent',
  };

  /* Returns the full recsys URL for *variant* */
  function getURL(variant) {
    const endpoint = VARIANT_TO_RECSYS_ENDPOINT[variant];
    let url = buildRecsysQuery(endpoint, { mode: 'mobile' });

    return url;
  }

  /* HACK HACK HACK HACK
   * This is a short term solution for our MVP experiment.
   * If this experiment is successful, we will roll out a long term solution
   * which transforms the result in the backend (sca, weaver, recsys) following the established patterns
   * HACK HACK HACK HACK
   */
  const DEFAULTDATA = {
    aria_title: 'Shopping',
    component_name: 'packages',
    countdown: 0,
    cta: {
      text: 'See more Shopping',
      url: 'https://www.buzzfeed.com/shopping',
    },
    data_source: 'recsys_api',
    package_id: '',
    results: [],
    template: 'default',
    theme: 'blue',
    title: '',
    treatments: ['shopping', 'visual'],
  };
  // fetch and transform package data
  const fetchData = async (variant, originStr, primeDayVariant = 'control') => {
    const url = getURL(primeDayVariant === 'time_spent' ? primeDayVariant : (variant || 'control'));
    let data = null;

    try {
      const response = await fetch(url, {});
      let recsysData = await response.json();
      let filteredResults = recsysData.results || [];

      // RT-2238:
      // no_primeday: filter out items with prime_day laser tag
      // time_spent: sort by time spent on page in last 5 days, desc
      let primeDayResults = [];
      if (primeDayVariant === 'no_primeday') {
        primeDayResults = filteredResults.filter(post =>
          !(
            Array.isArray(post.laser_tags) &&
            post.laser_tags.some(tag =>
              tag.tag_name === 'prime_day' &&
              tag.namespace_name === 'bf_content_description'
            )
          )
        );
        // If fewer than 6, top up from the end of all results
        if (primeDayResults.length < 6) {
          const needed = 6 - primeDayResults.length;
          const remainingPosts = filteredResults.filter(post =>
            !primeDayResults.some(p => p.id === post.id)
          );
          const extraPosts = remainingPosts.slice(-needed);
          primeDayResults = primeDayResults.concat(extraPosts);
        }

        filteredResults = primeDayResults;
      }

      // Return early if no results after filtering
      if (!filteredResults.length) {
        return null;
      }

      data = DEFAULTDATA;

      // set the package_id to be the first buzz_id
      data.package_id = filteredResults[0].id;

      // add origin params to the urls and use a subset of the recsys data
      data.results = filteredResults.map(function (post) {
        // Safely extract the "big" thumbnail (or fallback)
        let thumbnail = post?.image;
        let altText = post?.image_alt;

        if (Array.isArray(post.thumbnails) && post.thumbnails.length) {
          const thumbObj = post.thumbnails[0];
          if (Array.isArray(thumbObj.sizes) && thumbObj.sizes.length) {
            const bigThumb = thumbObj.sizes.find(s => s.size === 'big') || thumbObj.sizes[0];
            if (bigThumb) {
              thumbnail = bigThumb.url;
              altText = bigThumb.alt_text || '';
            }
          }
        }

        return {
          author: '',
          canonical_path: post.canonical_path,
          created_at: '',
          data_source: post.data_source_name,
          data_source_name: post.data_source_name,
          description: stripTags(post.description),
          id: post.id,
          image: thumbnail,
          image_alt_text: altText,
          name: post.name,
          url: post.url
            ? `${post.url}?origin=${originStr}`
            : `https://www.buzzfeed.com${post.canonical_path}?origin=${originStr}`,
        };
      });

      // add origin data to the cta url
      if (data.cta && data.cta.url) {
        data.cta.url += `?origin=${originStr}`;
      }
    } catch (error) {
      if (CLUSTER === 'dev') {
        console.log('error fetching data', error);
      }
    }
    return data;
  };

  return {
    fetchData: fetchData,
  };
})();

export function usePackage({ buzz }) {
  const [packageData, setPackageData] = useState(null);
  const { getExperimentValue, experiments } = useContext(ABeagleContext);
  const countryCode = buzz.country_code;
  const isUS = buzz.isUS;
  let category = (buzz.classification.section || buzz.category).toLowerCase();

  // collect buzz Laser Tags from bf_content_description namespace and topic tag_type
  const laserTags =
    (buzz.laser_tags &&
      buzz.laser_tags.bf_content_description &&
      buzz.laser_tags.bf_content_description.topic) ||
    [];

  // merge CMS tags and Laser tags together
  const buzzAndLaserTags = [
    ...buzz.tags.filter(tag => !tag.includes('--')),
    ...laserTags.map(tag => tag.tag_name),
  ];

  const { base_url } = useContext(DestinationContext);

  useEffect(() => {
    // we should load package only after ger experiments results from Abeagle
    if (!experiments.loaded) {
      return;
    }
    const isSensitive = buzz?.flags?.sensitive || false;
    const isAllBpageShoppingEnabled = buzz.isUS && !buzz.isAd && !isSensitive && !buzzAndLaserTags.includes('ai quiz') && !buzz.shouldHideRecircSection;
    const shoppingPackageVariant = getExperimentValue(
      'shopping-package-v4-ml-1499',
      { rejectErrors: false }
    );
    const primeDayVariant = getExperimentValue('RT-2238-remove-primeday-ynft', { rejectErrors: false });
    let packageCategory = '';


    if (isAllBpageShoppingEnabled || ((category === 'nifty' || category === 'shopping') && isUS)) {
      packageCategory = 'shopping';
    }

    // fetch package data
    /** Useful to fetch any specific package data
    const fetchData = async () => {
      const url = `${base_url}/site-component/v1/en-us/packages/${packageCategory}`;
      try {
        const response = await fetch(url, {});
        let data = await response.json();
        if ('results' in data && data.results.length) {
          data.results = data.results.map(function(post) {
            post.url += '?origin=' + originMaps(packageCategory, category);
            post.description = stripTags(post.description);
            return post;
          });
          if (data.cta && data.cta.url) {
            data.cta.url += '?origin=' + originMaps(packageCategory, category);
          }

          setPackageData({ ...data, category: packageCategory });
        }
      } catch (error) {
        if (CLUSTER === 'dev') {
          let data = testData;
          if ('results' in data && data.results.length) {
            data.results = data.results.map(function(post) {
              post.url += '?origin=' + originMaps(packageCategory, category);
              return post;
            });
            setPackageData({ ...data, category: packageCategory });
          }
        }
      }
    };
    */

    if (isUS && packageCategory === 'shopping') {
      const originStr = originMaps(packageCategory, category);
      ShoppingPackage.fetchData(shoppingPackageVariant, originStr, primeDayVariant).then(data => {
        if (data) {
          setPackageData({ ...data, category: packageCategory });
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    buzz.tags,
    category,
    isUS,
    countryCode,
    experiments,
  ]);

  return packageData;
}

function originMaps(packageCategory, buzzCategory) {
  const packageBuzzCategoryMap = {
    shopping: {
      shopping: 'bshp',
      nifty: 'nifty_pkg_bpage',
    },
  };
  const origins = {
    giftguide: 'bgg',
  };

  return packageBuzzCategoryMap[packageCategory] &&
    packageBuzzCategoryMap[packageCategory][buzzCategory]
    ? packageBuzzCategoryMap[packageCategory][buzzCategory]
    : origins[packageCategory] || 'shp';
}
